/**
 * Reconstructs the character a physical key would produce on a US ANSI layout.
 *
 * HID barcode/QR scanners emulate a keyboard and send key presses positioned on the
 * US layout. When the operating system runs a different layout (Czech QWERTZ for
 * instance) the browser composes completely different characters — `https://` arrives
 * as `https"--` and digits arrive as `+ěščřžýáíé`. Reading `KeyboardEvent.code`
 * instead of `KeyboardEvent.key` bypasses the OS layout entirely.
 */

const SHIFTED_DIGITS = [')', '!', '@', '#', '$', '%', '^', '&', '*', '('] as const;

const PUNCTUATION_KEYS: Record<string, readonly [string, string]> = {
    Backquote: ['`', '~'],
    Minus: ['-', '_'],
    Equal: ['=', '+'],
    BracketLeft: ['[', '{'],
    BracketRight: [']', '}'],
    Backslash: ['\\', '|'],
    Semicolon: [';', ':'],
    Quote: ['\'', '"'],
    Comma: [',', '<'],
    Period: ['.', '>'],
    Slash: ['/', '?'],
    Space: [' ', ' '],
};

/**
 * The numeric keypad is layout independent, so shift never changes its output.
 */
const NUMPAD_KEYS: Record<string, string> = {
    NumpadDivide: '/',
    NumpadMultiply: '*',
    NumpadSubtract: '-',
    NumpadAdd: '+',
    NumpadDecimal: '.',
};

const buildKeyMap = (): Record<string, readonly [string, string]> => {
    const map: Record<string, readonly [string, string]> = { ...PUNCTUATION_KEYS };

    for (let digit = 0; digit <= 9; digit += 1) {
        map[`Digit${digit}`] = [String(digit), SHIFTED_DIGITS[digit]];
        map[`Numpad${digit}`] = [String(digit), String(digit)];
    }

    for (let offset = 0; offset < 26; offset += 1) {
        const lower = String.fromCharCode(97 + offset);
        map[`Key${lower.toUpperCase()}`] = [lower, lower.toUpperCase()];
    }

    for (const [code, char] of Object.entries(NUMPAD_KEYS)) {
        map[code] = [char, char];
    }

    return map;
};

/**
 * Physical key code to its unshifted and shifted US character.
 */
export const US_LAYOUT_KEYS = buildKeyMap();

/**
 * Characters national QWERTZ layouts compose for the US key positions a scanner sends.
 *
 * Only the keys that actually differ from the US layout are listed; anything missing falls
 * back to `US_LAYOUT_KEYS`. Austria shares the German layout, and Slovak differs from Czech
 * in a handful of keys only.
 */
export const NON_US_LAYOUTS: Record<string, Record<string, readonly [string, string]>> = {
    cs: {
        Backquote: [';', '°'],
        Digit1: ['+', '1'],
        Digit2: ['ě', '2'],
        Digit3: ['š', '3'],
        Digit4: ['č', '4'],
        Digit5: ['ř', '5'],
        Digit6: ['ž', '6'],
        Digit7: ['ý', '7'],
        Digit8: ['á', '8'],
        Digit9: ['í', '9'],
        Digit0: ['é', '0'],
        Minus: ['=', '%'],
        Equal: ['´', 'ˇ'],
        BracketLeft: ['ú', '/'],
        BracketRight: [')', '('],
        Backslash: ['¨', '\''],
        Semicolon: ['ů', '"'],
        Quote: ['§', '!'],
        Comma: [',', '?'],
        Period: ['.', ':'],
        Slash: ['-', '_'],
        KeyY: ['z', 'Z'],
        KeyZ: ['y', 'Y'],
    },
    sk: {
        Backquote: [';', '°'],
        Digit1: ['+', '1'],
        Digit2: ['ľ', '2'],
        Digit3: ['š', '3'],
        Digit4: ['č', '4'],
        Digit5: ['ť', '5'],
        Digit6: ['ž', '6'],
        Digit7: ['ý', '7'],
        Digit8: ['á', '8'],
        Digit9: ['í', '9'],
        Digit0: ['é', '0'],
        Minus: ['=', '%'],
        Equal: ['´', 'ˇ'],
        BracketLeft: ['ú', '/'],
        BracketRight: ['ä', '('],
        Backslash: ['ň', ')'],
        Semicolon: ['ô', '"'],
        Quote: ['§', '!'],
        Comma: [',', '?'],
        Period: ['.', ':'],
        Slash: ['-', '_'],
        KeyY: ['z', 'Z'],
        KeyZ: ['y', 'Y'],
    },
    de: {
        Backquote: ['^', '°'],
        Digit1: ['1', '!'],
        Digit2: ['2', '"'],
        Digit3: ['3', '§'],
        Digit4: ['4', '$'],
        Digit5: ['5', '%'],
        Digit6: ['6', '&'],
        Digit7: ['7', '/'],
        Digit8: ['8', '('],
        Digit9: ['9', ')'],
        Digit0: ['0', '='],
        Minus: ['ß', '?'],
        Equal: ['´', '`'],
        BracketLeft: ['ü', 'Ü'],
        BracketRight: ['+', '*'],
        Backslash: ['#', '\''],
        Semicolon: ['ö', 'Ö'],
        Quote: ['ä', 'Ä'],
        Comma: [',', ';'],
        Period: ['.', ':'],
        Slash: ['-', '_'],
        KeyY: ['z', 'Z'],
        KeyZ: ['y', 'Y'],
    },
};

/**
 * National character back to the US character of the same physical key. The first key that
 * claims a character wins, so an ambiguous character keeps its most common meaning.
 */
const buildReverseMap = (layout: Record<string, readonly [string, string]>): Map<string, string> => {
    const map = new Map<string, string>();

    for (const [code, [unshifted, shifted]] of Object.entries(layout)) {
        const us = US_LAYOUT_KEYS[code];

        if (us === undefined) {
            continue;
        }

        if (unshifted !== us[0] && !map.has(unshifted)) {
            map.set(unshifted, us[0]);
        }

        if (shifted !== us[1] && !map.has(shifted)) {
            map.set(shifted, us[1]);
        }
    }

    return map;
};

/**
 * The other character of the same physical key, in both directions.
 *
 * A scanner typing a national layout has to hold shift for characters the US layout gives
 * unshifted — every digit and `/` on the Czech layout. When a shift press is lost, or stays
 * down over the next key, what arrives is the same key at the wrong level: `4` turns up as
 * `č`, `-` as `_`.
 */
const buildShiftPartnerMap = (layout: Record<string, readonly [string, string]>): Map<string, string> => {
    const map = new Map<string, string>();

    for (const [code, us] of Object.entries(US_LAYOUT_KEYS)) {
        const [unshifted, shifted] = layout[code] ?? us;

        if (unshifted === shifted) {
            continue;
        }

        if (!map.has(unshifted)) {
            map.set(unshifted, shifted);
        }

        if (!map.has(shifted)) {
            map.set(shifted, unshifted);
        }
    }

    return map;
};

/**
 * Everything known about one national layout: how to read a character as the US one of the
 * same key, and how to read it as that key pressed with the other shift state.
 */
type LayoutRepair = {
    reverse: Map<string, string>;
    shiftPartner: Map<string, string>;
};

const LAYOUT_REPAIRS: LayoutRepair[] = Object.values(NON_US_LAYOUTS).map(layout => ({
    reverse: buildReverseMap(layout),
    shiftPartner: buildShiftPartnerMap(layout),
}));

const REVERSE_LAYOUTS: Map<string, string>[] = LAYOUT_REPAIRS.map(repair => repair.reverse);

const translate = (text: string, reverse: Map<string, string>): string => {
    let result = '';

    for (const char of text) {
        result += reverse.get(char) ?? char;
    }

    return result;
};

/**
 * The text as it would have arrived on a US layout, once per supported national layout.
 *
 * The untouched input comes first, so a caller matching the candidates against an expected
 * shape never repairs a value that was already correct — which matters because `y` and `z`
 * are swapped on every QWERTZ layout and translating them is destructive.
 */
export const usLayoutCandidates = (text: string): string[] => {
    const candidates = [text];

    for (const reverse of REVERSE_LAYOUTS) {
        const translated = translate(text, reverse);

        if (!candidates.includes(translated)) {
            candidates.push(translated);
        }
    }

    return candidates;
};

/**
 * The reading of a character that fits `allowed`, looked for in the ways a scanner can get a
 * key wrong: the US character of the same key, the same key at the other shift level, and the
 * US character of that. Falls back to the plain translation when none of them fits.
 */
const repairChar = (char: string, repair: LayoutRepair, isAllowed: RegExp): string => {
    const translated = repair.reverse.get(char);

    if (translated !== undefined && isAllowed.test(translated)) {
        return translated;
    }

    const partner = repair.shiftPartner.get(char);

    if (partner !== undefined) {
        if (isAllowed.test(partner)) {
            return partner;
        }

        const translatedPartner = repair.reverse.get(partner);

        if (translatedPartner !== undefined && isAllowed.test(translatedPartner)) {
            return translatedPartner;
        }
    }

    return translated ?? char;
};

/**
 * The same alphabet without the flags that make a regex remember where it last matched.
 *
 * A global or sticky source pattern would carry `lastIndex` from one character to the next and
 * start reporting misses for characters it accepts.
 */
const withoutStatefulFlags = (allowed: RegExp): RegExp =>
    new RegExp(allowed.source, allowed.flags.replace(/[gy]/g, ''));

/**
 * Repairs only the characters that do not already belong to `allowed`, once per supported
 * national layout.
 *
 * Some scanners mangle part of a code and leave the rest intact — separators and digits that
 * happen to sit on unchanged keys come through correctly. Translating the whole string would
 * break those, so a character that already fits the expected alphabet is left alone.
 *
 * A character out of place is read back through both things that go wrong on a national
 * layout: the layout itself, and a shift press that was dropped or held over the next key.
 * The latter is what turns the `-` between the groups of a UUID into `_`, which would
 * otherwise leave nothing of the code to match.
 */
export const repairWithinAlphabet = (text: string, allowed: RegExp): string[] => {
    const candidates: string[] = [];
    const isAllowed = withoutStatefulFlags(allowed);

    for (const repair of LAYOUT_REPAIRS) {
        let result = '';

        for (const char of text) {
            result += isAllowed.test(char) ? char : repairChar(char, repair, isAllowed);
        }

        if (result !== text && !candidates.includes(result)) {
            candidates.push(result);
        }
    }

    return candidates;
};

const isAscii = (char: string): boolean => char.charCodeAt(0) < 128;

const translateChar = (char: string): string => {
    for (const reverse of REVERSE_LAYOUTS) {
        const us = reverse.get(char);

        if (us !== undefined) {
            return us;
        }
    }

    return char;
};

const isCapsLocked = (event: KeyboardEvent): boolean =>
    typeof event.getModifierState === 'function' && event.getModifierState('CapsLock');

const swapCase = (char: string): string => {
    const upper = char.toUpperCase();

    return char === upper ? char.toLowerCase() : upper;
};

/**
 * One key press of a scanner burst, kept as the key rather than as the character it produced.
 *
 * Which of the two characters a key carries actually arrived is the one thing a finished string
 * can no longer tell you, and it is exactly what scanners get wrong: a Shift press that is
 * dropped or held over the next key turns `-` into `_` and `4` into `$`. Keeping both readings
 * lets the decoder pick the one that belongs in the payload instead of guessing afterwards.
 */
export type ScannerKeyPressType = {

    /** Physical key as reported, empty when the driver reports none. */
    code: string;

    /** Shift level the press arrived with. */
    isShifted: boolean;

    /** US character of the key at the lower shift level. */
    unshifted: string;

    /** US character of the key at the upper shift level. */
    shifted: string;
};

/**
 * A scanner burst: the characters it decoded to, and the key presses they came from.
 *
 * `keys` is empty for a value that was typed or pasted rather than scanned, which leaves the
 * consumer with the string alone — the same thing it had before.
 */
export type ScannerScanType = {
    value: string;
    keys: readonly ScannerKeyPressType[];
};

/**
 * The key press behind a keyboard event, or null for keys that produce no character
 * (Enter, Tab, arrows, modifiers).
 *
 * Falls back to the browser-composed character when the physical key is unknown, so exotic
 * scanner output is degraded rather than dropped; such a press carries the same character at
 * both shift levels and is left to the character-level repair instead.
 */
export const resolveKeyPress = (event: KeyboardEvent): ScannerKeyPressType | null => {
    const key = US_LAYOUT_KEYS[event.code];

    if (key !== undefined) {
        // Nothing to do about CapsLock here: the key and the shift flag say what the scanner
        // sent, and CapsLock only changes the character the operating system composes from them.
        return {
            code: event.code,
            isShifted: event.shiftKey,
            unshifted: key[0],
            shifted: key[1],
        };
    }

    if (event.key.length !== 1) {
        return null;
    }

    // Some scanner drivers report no usable `code`, so the browser-composed character is all
    // there is. A non-ASCII character can only have come from a national layout and maps back
    // unambiguously; ASCII is left alone because `y` and `z` cannot be told apart out of context.
    // This is the one reading CapsLock does reach, and swapping the case of a letter undoes it.
    const composed = isCapsLocked(event) ? swapCase(event.key) : event.key;
    const char = isAscii(composed) ? composed : translateChar(composed);

    return { code: '', isShifted: false, unshifted: char, shifted: char };
};

/**
 * The printable character of a key press as it would be typed on a US layout, or
 * null for keys that produce no character.
 */
export const resolveUsLayoutChar = (event: KeyboardEvent): string | null => {
    const press = resolveKeyPress(event);

    if (press === null) {
        return null;
    }

    return press.isShifted ? press.shifted : press.unshifted;
};

/**
 * Every key read at the shift level it arrived with — the reading a well-behaved scanner gives.
 */
export const decodeStrict = (keys: readonly ScannerKeyPressType[]): string =>
    keys.map(press => (press.isShifted ? press.shifted : press.unshifted)).join('');

/**
 * Every key read at whichever of its two shift levels belongs to `allowed`.
 *
 * A lost Shift and a Shift held one key too long both land on the same physical key at the
 * wrong level, so the character that turns up is the payload character's neighbour — and the
 * neighbour of a payload character almost never belongs to the payload's own alphabet. Picking
 * the reading that does repairs both without guessing. A key whose level the alphabet cannot
 * decide, and one that arrived with no key code at all, keeps what it came with.
 */
export const decodeTolerant = (keys: readonly ScannerKeyPressType[], allowed: RegExp): string => {
    const isAllowed = withoutStatefulFlags(allowed);

    let result = '';

    for (const press of keys) {
        const arrived = press.isShifted ? press.shifted : press.unshifted;
        const other = press.isShifted ? press.unshifted : press.shifted;

        result += !isAllowed.test(arrived) && isAllowed.test(other) ? other : arrived;
    }

    return result;
};

const isBlankPress = (press: ScannerKeyPressType): boolean =>
    (press.isShifted ? press.shifted : press.unshifted).trim() === '';

/**
 * The burst without the whitespace at its ends.
 *
 * The key presses are trimmed along with the characters, so every later reading of the keys
 * still lines up with the value — a scan suffixed with a space would otherwise decode back to
 * one that carries it.
 */
export const trimScan = (scan: ScannerScanType): ScannerScanType => {
    if (scan.keys.length === 0) {
        return { value: scan.value.trim(), keys: [] };
    }

    let start = 0;
    let end = scan.keys.length;

    while (start < end && isBlankPress(scan.keys[start])) {
        start += 1;
    }

    while (end > start && isBlankPress(scan.keys[end - 1])) {
        end -= 1;
    }

    const keys = scan.keys.slice(start, end);

    return { value: decodeStrict(keys), keys };
};

/**
 * The distinct readings of a burst: the strict one first, then one per alphabet.
 *
 * The strict reading leads so that a burst which already reads correctly is never repaired,
 * the same rule `usLayoutCandidates` follows for whole strings.
 */
export const decodeCandidates = (keys: readonly ScannerKeyPressType[], alphabets: readonly RegExp[]): string[] => {
    if (keys.length === 0) {
        return [];
    }

    const candidates = [decodeStrict(keys)];

    for (const alphabet of alphabets) {
        const decoded = decodeTolerant(keys, alphabet);

        if (!candidates.includes(decoded)) {
            candidates.push(decoded);
        }
    }

    return candidates;
};
